import { useParams } from 'react-router';
import { useApiCall } from '../apiCall';
import {
    EmployeeFinanceService,
    type EmployeeWagesInput,
    type EmployeeWagesRequest,
} from 'src/api/EmployeeFinanceService';
import { useEffect, useMemo, useRef, useState } from 'react';
import { invalidateEmployeeWages } from '@stores/employeeFinanceStore';

const useSaveEmployeeWages = (
    employeeWagesToSave: EmployeeWagesInput,
): {
    departmentId: string | undefined;
    isEmployeeWagesBeingSaved: boolean;
    savingProgress: {
        completed: number;
        total: number;
        failedSavesEmployeeIds: Set<string>;
    };
    saveEmployeeWages: () => void;
} => {
    const { departmentId } = useParams<{
        departmentId: string;
    }>();

    const [savingProgress, setSavingProgress] = useState({
        completed: 0,
        total: 0,
        failedSavesEmployeeIds: new Set<string>(),
    });

    const param: EmployeeWagesRequest = useMemo(
        () => ({
            // Route param is always present on the pages using this hook
            departmentId: departmentId ?? '',
            employeeWages: employeeWagesToSave,
            setSavingProgress,
        }),
        [departmentId, employeeWagesToSave],
    );

    const {
        isLoading: isEmployeeWagesBeingSaved,
        requestApiCall: saveEmployeeWages,
    } = useApiCall(
        EmployeeFinanceService.saveEmployeeWages,
        [param],
        [param],
        false,
    );

    /*
     * saveEmployeeWages resolves without a response body, so success is
     * detected through the loading transition: when a save finishes,
     * drop the cached wages. The cache reflects whatever
     * was actually saved on the next read).
     */
    const wasSavingRef = useRef(false);

    useEffect(() => {
        const hasSaveJustCompleted =
            wasSavingRef.current && !isEmployeeWagesBeingSaved;

        if (hasSaveJustCompleted && departmentId) {
            invalidateEmployeeWages(departmentId);
        }

        wasSavingRef.current = isEmployeeWagesBeingSaved;
    }, [isEmployeeWagesBeingSaved, departmentId]);

    return {
        departmentId,
        isEmployeeWagesBeingSaved,
        savingProgress,
        saveEmployeeWages,
    };
};

export default useSaveEmployeeWages;
